// src/components/dlog/DlogUploader.tsx
import React, { useState, useCallback, useRef } from 'react';
import { DlogItem } from '../../models/Dlog';
import { v4 as uuidv4 } from 'uuid';
import FileUploader from '../common/FileUploader'; // Import the new component
import './Dlog.css'; // Keep Dlog specific styles

interface DlogUploaderProps {
  onLogLoaded: (logItem: DlogItem) => void;
}

// Basic analysis function (can be expanded significantly)
const analyzeLogContent = (content: string, fileName?: string): string => {
  // Check filename first for common types
  const lowerFileName = fileName?.toLowerCase();
  if (lowerFileName?.endsWith('.json')) return 'JSON';
  if (lowerFileName?.endsWith('.xml')) return 'XML';
  if (lowerFileName?.endsWith('.csv')) return 'CSV';
  if (lowerFileName?.endsWith('.log')) return 'Log File'; // Generic log

  // ToDo: handle compressed files

  // Then try content analysis
  try {
    // Basic JSON check
    if (content.trim().startsWith('{') || content.trim().startsWith('[')) {
        JSON.parse(content);
        return 'JSON';
    }
  } catch (e) {
    // Ignore JSON parse errors
    console.log( `analyzeLogContent: failed to parse ${fileName} as JSON. Ignoring failure.`);
  }

  // Add more checks (XML structure, CSV delimiters, specific log patterns)
  // Example: if (content.includes('<') && content.includes('>')) return 'XML-like';

  // Check for binary-like characters (simple heuristic)
  // eslint-disable-next-line 
  if (/[\u0000-\u0008\u000E-\u001F]/.test(content.substring(0, 500))) {
      return 'Binary/Unknown';
  }

  return 'Plain Text';
};

const DlogUploader: React.FC<DlogUploaderProps> = ({ onLogLoaded }) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);

  // This function now takes the content and optional filename, creates DlogItem
  const processLogContent = useCallback((content: string, fileName?: string) => {
    setProcessingError(null); // Clear previous processing errors
    try {
        const lines = content.split('\n');
        const newItem: DlogItem = {
          id: uuidv4(),
          index: 0, // index will be updated in DlogPage's handleLogLoaded
          name: fileName || `Pasted Text ${new Date().toLocaleTimeString()}`,
          contentType: fileName ? 'File' : 'Text',
          dlogType: analyzeLogContent(content, fileName),
          sizeBytes: new Blob([content]).size, // Calculate size
          lineCount: lines.length,
          loadedTime: new Date(),
          content: content, // Store the full content
        };

        // Truncate name if needed
        if (newItem.name.length > 40) {
            newItem.name = newItem.name.substring(0, 37) + '...';
        }
        onLogLoaded(newItem); // Pass the fully formed item up
    } catch (error) {
        console.error("Error processing log content:", error);
        setProcessingError(`Failed to process the log content in ${fileName}`);
    }
  }, [onLogLoaded]);

  // Callback for the FileUploader component
  const handleFileSelected = useCallback((file: File) => {
    setProcessingError(null);
    const reader = new FileReader();

    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (typeof content === 'string') {
        processLogContent(content, file.name);
      } else {
        console.error("FileReader did not return string content for:", file.name);
        setProcessingError(`Could not read file '${file.name}' as text.`);
      }
    };

    reader.onerror = (e) => {
        console.error("FileReader error:", reader.error);
        setProcessingError(`Error reading file '${file.name}'. It might be binary or corrupted.`);
    };

    // Read the file as text - suitable for logs
    reader.readAsText(file);

  }, [processLogContent]);

  // Handler for pasting text directly
  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pastedText = event.clipboardData.getData('text');
      if (pastedText && textAreaRef.current) {
          processLogContent(pastedText); // Process pasted text directly
          // Optionally clear the textarea after paste processing
          setTimeout(() => {
              if(textAreaRef.current) textAreaRef.current.value = '';
          }, 0);
      }
  };

  return (
    // Container for both file upload and text paste areas
    <div className="dlog-uploader-container">
        {/* Use the reusable FileUploader */}
        <FileUploader
            onFileSelected={handleFileSelected}
            acceptedFileTypes=".log,.txt,.json,.xml,text/*" // Specific types for logs
            maxFileSize={10 * 1024 * 1024} // Example: 10MB limit
            label="Drag & Drop Log File"
            buttonText="Select Log File"
            className="dlog-file-uploader-instance" // Add specific class if needed
        />

        <p className="dlog-separator-text">or</p>

        {/* Keep the text paste area */}
        <textarea
            ref={textAreaRef}
            placeholder="Paste log text here"
            onPaste={handlePaste}
            rows={5} // Adjusted rows
            className="dlog-paste-area" // Keep specific class
        />
        {processingError && <p style={{ color: 'red', marginTop: '0.5rem', textAlign: 'center' }}>{processingError}</p>}
    </div>
  );
};

export default DlogUploader;

